import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Typography,
  Link,
  CircularProgress,
  Alert,
  Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';

// Styled components
const FormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}));

const PasswordStrengthIndicator = styled(Box)(({ theme, strength }) => {
  const getColor = () => {
    switch (strength) {
      case 'weak':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'strong':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[300];
    }
  };

  return {
    height: 4,
    width: '100%',
    backgroundColor: theme.palette.grey[200],
    borderRadius: 2,
    position: 'relative',
    marginTop: theme.spacing(1),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: strength === 'weak' ? '33%' : strength === 'medium' ? '66%' : strength === 'strong' ? '100%' : '0%',
      backgroundColor: getColor(),
      borderRadius: 2,
      transition: 'width 0.3s ease'
    }
  };
});

const LoginForm = ({ onSubmit, loading, error, clearError }) => {
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formError, setFormError] = useState('');
  const [touched, setTouched] = useState({
    email: false,
    password: false
  });

  // Email validation
  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  const getPasswordStrength = (password) => {
    if (!password) return '';
    if (password.length < 6) return 'weak';
    if (password.length < 10) return 'medium';
    return 'strong';
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear errors when user starts typing
    if (formError) setFormError('');
    if (error) clearError();
  };

  // Handle input blur
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Toggle remember me
  const toggleRememberMe = () => {
    setRememberMe(!rememberMe);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.email || !formData.password) {
      setFormError('Please enter both email and password');
      return;
    }
    
    if (!isEmailValid(formData.email)) {
      setFormError('Please enter a valid email address');
      return;
    }
    
    // Submit the form
    onSubmit(formData.email, formData.password, rememberMe);
  };

  return (
    <FormContainer component="form" onSubmit={handleSubmit}>
      {/* Error message */}
      {(error || formError) && (
        <Fade in={Boolean(error || formError)}>
          <Alert 
            severity="error" 
            sx={{ width: '100%' }}
            onClose={() => formError ? setFormError('') : clearError()}
          >
            {formError || error}
          </Alert>
        </Fade>
      )}
      
      {/* Email field */}
      <TextField
        required
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        autoComplete="email"
        autoFocus
        value={formData.email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.email && (!formData.email || !isEmailValid(formData.email))}
        helperText={
          touched.email && !formData.email 
            ? 'Email is required' 
            : touched.email && !isEmailValid(formData.email) 
              ? 'Please enter a valid email' 
              : ' '
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      
      {/* Password field */}
      <TextField
        required
        fullWidth
        name="password"
        label="Password"
        type={showPassword ? 'text' : 'password'}
        id="password"
        autoComplete="current-password"
        value={formData.password}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.password && !formData.password}
        helperText={touched.password && !formData.password ? 'Password is required' : ' '}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={togglePasswordVisibility}
                edge="end"
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      {/* Password strength indicator (only show when typing) */}
      {formData.password && (
        <>
          <PasswordStrengthIndicator strength={getPasswordStrength(formData.password)} />
          <Typography variant="caption" color="text.secondary">
            Password strength: {getPasswordStrength(formData.password) || 'none'}
          </Typography>
        </>
      )}
      
      {/* Remember me checkbox */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={rememberMe} 
              onChange={toggleRememberMe} 
              color="primary" 
              name="rememberMe" 
            />
          }
          label="Remember me"
        />
        <Link href="/forgot-password" variant="body2">
          Forgot password?
        </Link>
      </Box>
      
      {/* Submit button */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        startIcon={loading ? null : <LoginIcon />}
        sx={{ 
          mt: 1, 
          py: 1.5,
          position: 'relative'
        }}
      >
        {loading ? (
          <CircularProgress 
            size={24} 
            sx={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-12px',
              marginLeft: '-12px'
            }} 
          />
        ) : 'Sign In'}
      </Button>
    </FormContainer>
  );
};

export default LoginForm;
